import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridContainer, Grid } from '../../shared/grid';
import { useStoreContext } from '../../store/context';
import { Autocomplete, TextField, Button, IconButton } from '@mui/material';
import {errorResponse, ValidCharacters, validateCharacters, formatPhoneNumber, fieldSampleGroupEditor } from '../../store/common';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import CustomTablePagination from '../../shared/TablePagination/CustomTablePagination';
import { useAddAlert, useUser} from '../../store/actions';
import LoadingSpinner from 'src/shared/loading/loading';
import {  getArea, getContactsCall, getGroups } from 'src/apis/getFuelSampleCalls';
import './Contacts.scss'
import { ContactRow } from './ContactRow';
import { AddContact } from './AddContact';
import { alertMessages } from 'src/constants';
import { modalStyle } from 'src/shared/Styles/Styles';
import { deleteContact } from 'src/apis/mutationsFuelSample';

/***
 * This is the Field Sample child Page
 * The route will have to be fixed from when a user clicks on it from
 * the pop up station route and coming from the header.
 * 
 */

/***
 * Mock data
 * 
 */
interface Data {
  blank_column: string,
  name: string,
  location: string,
  phone: number,
  extension?: number,
  title: string,
  email: string,
  areas: string,
  groups: string,
}

type Order = 'asc' | 'desc';
/***
 * Following is used as an Enhanced header to filter the headers as needed
 * 
 */
interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'blank_column',
    numeric: false,
    disablePadding: true,
    label: 'Null',
  },
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'NAME',
  },
  {
    id: 'areas',
    numeric: false,
    disablePadding: false,
    label: 'AREA',
  },
  {
    id: 'groups',
    numeric: false,
    disablePadding: false,
    label: 'GROUP',
  },
  {
    id: 'phone',
    numeric: false,
    disablePadding: false,
    label: 'PHONE',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'EMAIL',
  },
  {
    id: 'blank_column',
    numeric: false,
    disablePadding: true,
    label: 'Null',
  },
];

interface EnhancedTableProps {
  order: Order;
  orderBy: string;
}
/***
 * Used for styling the Cells for the inner tables
 * 
 */

function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy}=
    props;

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          headCell.label === 'Null' ?
            (
              <td key ={`${headCell.id}-check`} style={{padding: 'checkbox'}} className="headerParent" >&nbsp;</td>
            ) : (
              <TableCell
                tabIndex={0}
                className='headerParent'
                key={headCell.id}
                align={headCell.numeric ? 'right' : 'left'}
                padding={headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.id ? order : false}

              >
                  {headCell.label}
              </TableCell>
            )
        ))}
      </TableRow>
    </TableHead>
  );
}

/***
 * This is the Field Sample parent Page
 * The route will have to be fixed from when a user clicks on it from
 * the pop up station route and coming from the header.
 * 
 */


export const Contacts: React.FC = () => {
  const [order] = useState<Order>('asc');
  const [orderBy] = useState<keyof Data>('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [openAddContactModal, setOpenAddContactModal] = useState(false);
  const [editContactBoolean, setEditContactBoolean] = useState<boolean>(false);
  const [contactToEdit, setContactToEdit] = useState<any>({});

	const [state,dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);
  const setUser = useUser(dispatch);
  const [token, setToken] = useState<string|null>(null);

  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsCallComplete, setContactsCallComplete] = useState<boolean>(false);
  const [contactFailureCount, setContactFailureCount] = useState(0);
  const [firstGetContactTableCall, setFirstGetContactTableCall] = useState<boolean>(false)

  const [completeContactsList, setCompleteContactsList] = useState<any[]>([]);
  const [searchContact, setSearchContact] = useState<any>(null);

  const [createUpdateContactSuccessful, setCreateUpdateContactSuccessful] = useState<boolean>(false)
  const [callMadeForAllContacts, setCallMadeForAllContacts] = useState<boolean>(false);
  
  const allObjArea =  useMemo(()=>({areaName: "All",areaId: "All"}),[]);
  const [, setCallComplete] = useState<boolean>(true);

  const [areaList, setAreaList] = useState<any[]>([allObjArea]);
  const [areaSelection, setAreaSelection] = useState<any>(areaList[0]);
  const [inputArea, setInputArea] = useState('');
  const [areaFailureCount, setAreaFailureCount] = useState(0);

  const allObjGroup = useMemo(()=>({groupName: "All", groupId: 0, status: "Active", area:{areaId:'All', areaName: 'All'}}),[]);
  const [groupList, setGroupList] = useState<any[]>([allObjGroup]);
  const [inputGroupValue, setInputGroupValue] = useState('');
  const [groupFailureCount, setGroupFailureCount] = useState(0);
  const [groupSelection, setGroupSelection] =useState<any>(groupList[0])


  const findUniqueContacts = (array: any, props: any[])=>{
    const seen = new Set();
    return array?.filter(obj=>{
        const key = props.map(prop=>obj[prop]).join('|')
        return seen.has(key)?false:seen.add(key)
      
    })
  }

  const getGroupCallResponse = useCallback((request: any, areaIdParam: any) => {
    (async () => {
      try {
        const groups: any = await getGroups(request, areaIdParam);
        const groupsUniqueName:any[] = [...new Map(groups?.map(item =>
         [item['groupName'], item])).values()];
        groupsUniqueName?.sort(function(a, b) {
          return a.groupName.localeCompare(b.groupName);
       })
        groupsUniqueName?.unshift(allObjGroup);
        setGroupList(groupsUniqueName);
      }
      catch (error) {
        setGroupFailureCount(groupFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  }, [allObjGroup, groupFailureCount, addAlert]);

    //**this gets the response from the area call to populate the area dropdown */
    const getAreaCallResponse = useCallback((request: any, areaIdParam?: string) => {
      (async () => {
        try {
          const area: any = await getArea(request, areaIdParam);
          area.unshift(allObjArea);
          setAreaList(area);
          if (!areaIdParam) {
            const requestGroup ={
              token: null,
              addAlert:addAlert,
              setCallComplete: setCallComplete,
              setGroupFailureCount: setGroupFailureCount,
              groupFailureCount: groupFailureCount
            }
            getGroupCallResponse(requestGroup, areaSelection?.areaId)
          }
  
        }
        catch (error) {
          setAreaFailureCount(areaFailureCount + 1)
          errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
        }
      })();
    }, [allObjArea, areaFailureCount, addAlert, areaSelection, groupFailureCount, getGroupCallResponse]);

  /** Make an API call to get all contacts  if forTable is true that will populate the table
   * if it is false it will populate the dropdowns and search dir
  */
  const getContacts = useCallback((request: any, forTable:boolean) => {
    if(!forTable){
      setCallMadeForAllContacts(true)
    }
    (async () => {
      try {
        const contactPromise: any = await getContactsCall(request);
        setContactsCallComplete(true);
        let tempContacts = [...contacts];

        if(forTable){
          if(tempContacts && tempContacts.length>0){
            if(contactPromise?.contacts){
              let tempContactRes = contactPromise.contacts;
              tempContactRes.forEach((tContact:any)=>{
                let matchContactId =tempContacts.find( (c) => c.contactId ===tContact.contactId )
                if(matchContactId){
                  tContact.openContactAccordion = !matchContactId.openContactAccordion? false: matchContactId.openContactAccordion
                }
                else{
                  tContact.openContactAccordion = false;
                }
              });
              setContacts(tempContactRes);

            }
            else{
              let tempContactRes = contactPromise?.contacts;
              tempContactRes.forEach((contact)=>{
                contact.openContactAccordion = false;
              })
              setContacts(tempContactRes);

            }
          }
          else{
            let tempContactRes = contactPromise?.contacts;
            tempContactRes.forEach((contact)=>{
              contact.openContactAccordion = false;
            })
            setContacts(tempContactRes);
          }
          setTotalCount(contactPromise?.pageInfo.total_count);
        }
        else{
          let unique = findUniqueContacts(contactPromise?.contacts, ['contactName', 'location', 'contactEmail', 'contactPhone', 'contactExtension', 'title']);
          let cList = unique?.map((option:any) => ({
              id: option.contactId,
              label: `${option.contactName? option.contactName :''}${option.location? '-'+option.location:''}${checkPhoneExt(option?.contactPhone, option?.contactExtension)}${option.title? '-'+option.title: ''}${option.contactEmail? '-'+option.contactEmail:''}`,
            })
          );
          const sortedList = cList.sort(function(a, b) {
            return a.label.localeCompare(b.label);
         });
          setCompleteContactsList(sortedList);
          setCallMadeForAllContacts(false)

        }
      } catch (error) {
        setContactsCallComplete(true);
        setContactFailureCount(contactFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.retrieveContacts);
        setCallMadeForAllContacts(false)
      }
    })();
  }, [setContactsCallComplete, setContacts, contactFailureCount, setContactFailureCount, addAlert, contacts]);

   /*set contacts location, search directory, and title dropdowns*/

  useEffect(() => {
    if (completeContactsList?.length <1 && !callMadeForAllContacts) {
      const contactForDropdowns = {
        setContactCallComplete: setContactsCallComplete,
        addAlert: useAddAlert,
        token: token,
        setContactFailureCount: setContactFailureCount,
        contactFailureCount: contactFailureCount,
        returnAll: true,
        page: null, 
        perPage: null,
        contactId: null,
        filterByLocation:null,
        filterByTitle: null,
        filterByGroup: groupSelection?.groupName,
        filterByArea: areaSelection?.areaName,
      };
      setContactsCallComplete(false);
      getContacts(contactForDropdowns, false);
      const requestAreaReq = {
        setCallComplete: setCallComplete,
        addAlert: addAlert,
        token: null,
        setAreaFailureCount: setAreaFailureCount,
        areaFailureCount: areaFailureCount
      }
      getAreaCallResponse(requestAreaReq)
    }
    
  }, [completeContactsList, getContacts, token, callMadeForAllContacts,setContactFailureCount, contactsCallComplete, contactFailureCount, page, rowsPerPage,
    addAlert, areaFailureCount, getAreaCallResponse, areaSelection, groupSelection,
  ]);

    /** Set Contacts for the table*/

  useEffect(() => {
    if (contacts?.length <1 && contactFailureCount <= 2 && !firstGetContactTableCall) {
      setContactsCallComplete(false);
      const requestContactReq = {
        setContactCallComplete: setContactsCallComplete,
        addAlert: useAddAlert,
        token: token,
        setContactFailureCount: setContactFailureCount,
        contactFailureCount: contactFailureCount,
        returnAll: false,
        page: page, 
        perPage: rowsPerPage,
        contactId: null,
        filterByLocation:null,
        filterByTitle: null,
        filterByGroup: groupSelection?.groupName,
        filterByArea: areaSelection?.areaName,
      };
      getContacts(requestContactReq, true);
      setFirstGetContactTableCall(true)
    }
    
  }, [contacts, getContacts, token, setContactFailureCount, contactsCallComplete, contactFailureCount, page, rowsPerPage, firstGetContactTableCall,areaSelection, groupSelection,]);

  useEffect(()=>{
    /** find access token and set to send to bearer */
    if(state.userTokens && token) {
      /** If the Token is refreshed this will set the @param token equal to updated value */
      setToken(state.userTokens.accessToken);
    }
    else if(localStorage.getItem('user') && state.userTokens && !token){
      /** find access token and set to send to bearer */
      const parsedSession = JSON.parse(localStorage.getItem('user')??'');
      setUser(parsedSession.userInfo);
      setToken(state.userTokens.accessToken);
    }
    else if(!state.user){
      const parsedSession = JSON.parse(localStorage.getItem('user')??'')
      setUser(parsedSession.userInfo)    
    }
  },[state.userTokens, setUser, setToken, state.user, token]);

  /* contact table if new contact is edited or created*/
  useEffect(()=>{
    if(createUpdateContactSuccessful){
      setContactsCallComplete(false);
      const contactAddedEdited = {
        setContactCallComplete: setContactsCallComplete,
        addAlert: useAddAlert,
        token: token,
        setContactFailureCount: setContactFailureCount,
        contactFailureCount: contactFailureCount,
        returnAll: false,
        page: page, 
        perPage: rowsPerPage,
        contactId: searchContact?.id,
        filterByGroup: groupSelection?.groupName,
        filterByArea: areaSelection?.areaName,
      };
      
      getContacts(contactAddedEdited, true);
      const contactForDropdowns = {
        setContactCallComplete: setContactsCallComplete,
        addAlert: useAddAlert,
        token: token,
        setContactFailureCount: setContactFailureCount,
        contactFailureCount: contactFailureCount,
        returnAll: true,
        page: null, 
        perPage: null,
        contactId: null,
        filterByLocation:null,
        filterByTitle: null,
      };
      getContacts(contactForDropdowns, false);
      setCreateUpdateContactSuccessful(false);
    }
  },[ contactFailureCount, createUpdateContactSuccessful, getContacts, page, rowsPerPage, searchContact?.id, token,areaSelection, groupSelection,])

  const handleOpenContact = () => {
    setEditContactBoolean(false);
    setContactToEdit({});
    setOpenAddContactModal(true);
  };
  const editContact = (contact: any) =>{
    setEditContactBoolean(true);
    setContactToEdit(contact);
    setOpenAddContactModal(true);
  }

  const handleCloseAddContact = (_event: any, reason: any) => {
    if (reason && reason === "backdropClick") {
      setOpenAddContactModal(false);
    }

    setOpenAddContactModal(false);
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    setContactsCallComplete(false);
    const updatePageCall = {
      setContactCallComplete: setContactsCallComplete,
      addAlert: useAddAlert,
      token: token,
      setContactFailureCount: setContactFailureCount,
      contactFailureCount: contactFailureCount,
      returnAll: false,
      page: newPage, 
      perPage: rowsPerPage,
      contactId: searchContact?.id,
      filterByGroup: groupSelection?.groupName,
      filterByArea: areaSelection?.areaName,
    };
    getContacts(updatePageCall, true);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
    const updatePerPageCall = {
      setContactCallComplete: setContactsCallComplete,
      addAlert: useAddAlert,
      token: token,
      setContactFailureCount: setContactFailureCount,
      contactFailureCount: contactFailureCount,
      returnAll: false,
      page: 0, 
      perPage: Number.parseInt(event.target.value, 10),
      contactId: searchContact?.id,
      filterByGroup: groupSelection?.groupName,
      filterByArea: areaSelection?.areaName,
    };
    setContactsCallComplete(false);
    getContacts(updatePerPageCall, true);
  };

  const handleSearchContacts=(e:React.SyntheticEvent<Element, Event>, val: any)=>{
    setSearchContact(val);
    setPage(0)
    const updateSearchedContact = {
      setContactCallComplete: setContactsCallComplete,
      addAlert: useAddAlert,
      token: token,
      setContactFailureCount: setContactFailureCount,
      contactFailureCount: contactFailureCount,
      returnAll: false,
      page: 0, 
      perPage: rowsPerPage,
      contactId: val?.id,
      filterByGroup: groupSelection?.groupName,
      filterByArea: areaSelection?.areaName,
    };
    setContactsCallComplete(false);
    getContacts(updateSearchedContact, true);

	}

  const checkPhoneExt = (phone: any, ext: any)=>{
    if(phone){
      if(ext){
        return '-'+ formatPhoneNumber(phone)+`ext.${ext}`
      }
      else{
        return '-'+ formatPhoneNumber(phone)
      }
    }
    else{
      return ''
    }
  }

  const deleteContactResponse = async (contactId: any) => {
    try {
      await deleteContact(state.user.token, addAlert, contactId);
  
      // Refresh the contacts data for the table
      const requestForTable = {
        setContactCallComplete: setContactsCallComplete,
        addAlert: useAddAlert,
        token: state.user.token,
        setContactFailureCount: setContactFailureCount,
        contactFailureCount: contactFailureCount,
        returnAll: false,
        page: page,
        perPage: rowsPerPage,
        contactId: searchContact?.id,
        filterByGroup: groupSelection?.groupName,
        filterByArea: areaSelection?.areaName,
      };
      getContacts(requestForTable, true);
  
      // Refresh the contacts data for the dropdown
      const requestForDropdowns = {
        setContactCallComplete: setContactsCallComplete,
        addAlert: useAddAlert,
        token: state.user.token,
        setContactFailureCount: setContactFailureCount,
        contactFailureCount: contactFailureCount,
        returnAll: true,
        page: null,
        perPage: null,
        contactId: null,
        filterByLocation: null,
        filterByTitle: null,
      };
      getContacts(requestForDropdowns, false);
      
    } catch (error) {
      errorResponse(error, addAlert, alertMessages.siteSampleData);
    }
  };

  return (
    <div className='table-page-container' data-testid={'table-page-container'} style={{overflowX: 'hidden'}}>
      <Modal //Modal to open the Area Modal
        className="smooth-scrollbar"
        open={openAddContactModal}
        onClose={handleCloseAddContact}
      >
        <div>
          <IconButton aria-label='close' style={{left: '75%', top: '5.5%', position: 'absolute', cursor: 'pointer'}} onClick={()=>{setOpenAddContactModal(false)}}>
            <CloseIcon style={{color: 'white'}}/>
          </IconButton>
          <Box sx={modalStyle} className="smooth-scrollbar removePadding" >
            {
              editContactBoolean?(
                <AddContact setOpen={setOpenAddContactModal} open={openAddContactModal} editContact={contactToEdit} setCreateUpdateContactSuccessful={setCreateUpdateContactSuccessful}/>

              ):(
                <AddContact setOpen={setOpenAddContactModal} open={openAddContactModal} setCreateUpdateContactSuccessful={setCreateUpdateContactSuccessful}/>
              )
            }
          </Box>
        </div>
      </Modal>

      <GridContainer className="margin-top-3 dropdown-style">
        <Grid row gap="md" style={{marginBottom: '2.5%'}}>
            <Grid desktop={{ col: 10 }}><span className="font-sans-xl text-bold">Contacts</span></Grid>
              {
                state.user && fieldSampleGroupEditor(state.user) && 
                <Grid style={{ textAlign: 'right' }}>
                  <Button variant="outlined" onClick={handleOpenContact}>Add Contact</Button>
                </Grid>
              }
        </Grid>
      
        <Grid row gap="md">
          <Grid desktop={{ col: 4 }}>
            <label htmlFor='search-contact-directory' className="font-sans-md text-bold">Search Directory</label>
          </Grid>
          <Grid desktop={{ col: 2 }}>
            <label htmlFor='search-contact-area' className="font-sans-md text-bold">Area</label>
          </Grid>
          <Grid desktop={{ col: 2 }}>
            <label htmlFor='search-contact-group' className="font-sans-md text-bold">Group</label>
          </Grid>
        </Grid>
        <Grid row gap="md" className="margin-top-1">

          {/*** SEARCH FIELD ***/}
          <Grid desktop={{ col: 4 }}>
            <div className='searchContacts'>
                <i className="fas fa-search" />
                <Autocomplete
                  freeSolo
                  onChange={(e, value) =>handleSearchContacts(e,value)}
                  noOptionsText={
                    <div className='no-results'>
                        <div>No results match your search criteria.</div>
                        <div><b>Here are a few tips:</b></div>
                        <div>• Remember to check your spelling<br />
                            • Ensure you are searching Contact Name<br/>
                            • Ensure you are searching Contact Location<br/>
                            • Ensure you are searching Government Phone<br/>
                            • Ensure you are searching Contact Title<br/>
                            • Ensure you are searching Government Email<br/>
                            </div>
                    </div>
                  }
                  aria-labelledby='search-contact-directory'
                  id='search-contact-directory'
                  options={completeContactsList}
                  value={searchContact}
                  renderInput={(params) => (
                    <TextField
                      label=""
                      onInput={(e) => validateCharacters(e, ValidCharacters.Alpha, ValidCharacters.Numeric, '.', ',', ' ', '-')}
                      placeholder='Search All Contact Details...'
                      data-testid={'search-all-contacts'}
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }}
                    />
                  )}
                />
              </div>
          </Grid>

           {/*** AREA INPUT FIELD ***/}
          <Grid desktop={{ col: 2 }}>
            <Autocomplete
              className='height-55'
              aria-labelledby='search-contact-area'
              id='search-contact-area'
              disableClearable
              value={areaSelection}
              onChange={(event: any, newValue: any) => {
                setAreaSelection(newValue);
                const requestGroup ={
                  token: null,
                  addAlert:addAlert,
                  setCallComplete: setCallComplete,
                  setGroupFailureCount: setGroupFailureCount,
                  groupFailureCount: groupFailureCount
                }
                getGroupCallResponse(requestGroup, newValue?.areaName);
                const areaUpdate ={
                    setContactCallComplete: setContactsCallComplete,
                    addAlert: useAddAlert,
                    token: token,
                    setContactFailureCount: setContactFailureCount,
                    contactFailureCount: contactFailureCount,
                    filterByGroup: groupSelection?.groupName,
                    filterByArea: newValue?.areaName,
                    returnAll: false,
                    page: 0, 
                    perPage: rowsPerPage,
                    contactId: searchContact?.id,
                };
                setContactsCallComplete(false);
                getContacts(areaUpdate, true);

                setPage(0);
              }}
              inputValue={inputArea}
              onInputChange={(event, newInputValue) => {
                setInputArea(newInputValue);
              }}
              getOptionLabel={(option) => option.areaName}
              options={areaList}
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.Alpha, ValidCharacters.Numeric, '.', ',', ' ', '-')}
                  data-testid={'area-search'}
                  {...params}
                  label=""
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </Grid>
          
          {/*** Group INPUT FIELD ***/}
          <Grid desktop={{ col: 2 }}>
            <Autocomplete
              className='height-55'
              disableClearable
              aria-labelledby='search-contact-group'
              id='search-contact-group'
              value={groupSelection}
              onChange={(event: any, newValue: any) => {
                setGroupSelection(newValue);
                const areaReq = {
                  setCallComplete: setCallComplete,
                  addAlert: addAlert,
                  token: token,
                  setAreaFailureCount: setAreaFailureCount,
                  areaFailureCount: areaFailureCount
                }
                if(newValue?.area.areaId ==='All'){
                  getAreaCallResponse(areaReq)
                }
                else{
                  getAreaCallResponse(areaReq, newValue?.area.areaId)
                }

                const groupUpdate = {
                  setContactCallComplete: setContactsCallComplete,
                  addAlert: useAddAlert,
                  token: token,
                  setContactFailureCount: setContactFailureCount,
                  filterByGroup: newValue?.groupName,
                  filterByArea: areaSelection?.areaName,
                  contactFailureCount: contactFailureCount,
                  returnAll: false,
                  page: 0,
                  perPage: rowsPerPage,
                  contactId: searchContact?.id,
                }
                setContactsCallComplete(false);
                getContacts(groupUpdate, true);
                setPage(0);
              }}
              getOptionLabel={(option) => option.groupName}
              options={groupList}
              inputValue={inputGroupValue}
              onInputChange={(event, newInputValue) => {
                setInputGroupValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.Alpha, ValidCharacters.Numeric,'.', ' ',)}
                  data-testid={'group-search'}
                  {...params}
                  label=""
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </Grid>
          <Grid desktop={{ col: 2 }} />
        </Grid>

      </GridContainer>

      <TableContainer component={Paper} className='margin-top-2 first-table MuiPaper-root-special'>
        {(!contactsCallComplete) && (

          <div style={{height: '30%'}}>
            <LoadingSpinner/>
          </div>
        )}

        <Table aria-label="Contacts" size="small">
          <colgroup>
            <col width="2%" />
            <col width="15%" />
            <col width="15%" />
            <col width="15%" />
            <col width="15%" />
            <col width="20%" />
            <col width="2%" />


          </colgroup>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
          />
          <TableBody>
            {contacts?.map((row) => {
              let params = {
                contact: row,
                contacts: contacts,
                setContacts: setContacts,
                editContact: editContact,
              }
              return(
                <ContactRow key ={row.name} allParams = {params} deleteContactResponse={deleteContactResponse}/>

              )
            }
            )}

          </TableBody>
        </Table>
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 30]}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />     
      </ TableContainer>
    </div>
  );
};